import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Upload,
  LogOut,
  Users,
  Key,
  Menu,
  X,
  Mail,
  ChevronDown,
  ArrowLeft,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { MailPlus, MessageSquare } from "lucide-react";
import logo from "../assets/logo.png";

// Import forms
import Step1 from "../pages/Registration/RegisterForm";
import Step2 from "../pages/Registration/RegisterPage1";
import Step3 from "../pages/Registration/RegisterPage2";
import Step4 from "../pages/Registration/RegisterPage3";
import Step5 from "../pages/Registration/RegisterPage4";
import AllStudentsForm from "../pages/Agent-Student";
import LogoutSection from "../assets/logout";
import Header from "../assets/Header";
import Footer from "../assets/Footer";
import Profile from "../assets/profile";
import ChangeP from "../assets/changepassword";
import AgentAnalyticsDashboard from "../pages/Agent-DahsboardS";
import MessagesInbox from "../pages/Admin/MessagesInbox";
import MessageThread from "../pages/Admin/MessageThread";
import NewMessage from "../pages/Admin/NewMessage";

// Theme constants
const THEME = {
  primary: "#2563eb",
  secondary: "#1e40af",
  background: "#f8fafc",
  sidebar: "#1f2937",
  card: "#ffffff",
  textPrimary: "#1e293b",
  textSecondary: "#64748b",
  success: "#10b981",
  warning: "#f59e0b",
  error: "#ef4444",
  borderRadius: "8px",
  shadow: "0 4px 6px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.1)",
  header: {
    background: "linear-gradient(to right, #0c4a6e, #3730a3, #1f2937)",
    textPrimary: "#f1f5f9",
    textSecondary: "#dbeafe",
    border: "rgba(30, 64, 175, 0.4)",
    highlight: "#22c55e",
    logoShadow: "0 0 6px rgba(59, 130, 246, 0.5)",
    hoverBg: "rgba(55, 65, 81, 0.7)",
    focusRing: "rgba(96, 165, 250, 1)",
  },
};

const spring = { type: "spring", stiffness: 200, damping: 24 };

const CollapsibleMenu = ({
  title,
  icon: Icon,
  keyName,
  items,
  activeSection,
  setActiveSection,
  setSidebarOpen,
}) => {
  const [isOpen, setIsOpen] = useState(activeSection.startsWith(keyName));

  useEffect(() => {
    setIsOpen(activeSection.startsWith(keyName));
  }, [activeSection, keyName]);

  return (
    <div className="space-y-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full text-left flex items-center justify-between gap-2 text-sm py-2 px-3 transition-colors duration-300 rounded-lg ${
          activeSection.startsWith(keyName)
            ? "bg-blue-600 text-white"
            : "hover:bg-gray-700 text-gray-100"
        }`}
      >
        <div className="flex items-center gap-2">
          <Icon size={14} aria-hidden="true" />
          <span>{title}</span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 0 : -90 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown size={14} />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden pl-6"
          >
            {items.map(({ key, icon: ItemIcon, label }) => (
              <motion.button
                key={key}
                onClick={() => {
                  setActiveSection(key);
                  setSidebarOpen(false);
                }}
                className={`w-full text-left flex items-center gap-2 text-sm py-2 px-3 transition-colors duration-300 rounded-lg ${
                  activeSection === key
                    ? "bg-blue-700 text-white"
                    : "hover:bg-gray-700 text-gray-200"
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={spring}
              >
                <ItemIcon size={12} aria-hidden="true" />
                <span>{label}</span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function SubAdminDashboard() {
  const [activeSection, setActiveSection] = useState("analytics");
  const [regStep, setRegStep] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const hamburgerRef = useRef(null);
  const [inboxRefreshTrigger, setInboxRefreshTrigger] = useState(0);
  const [navigationParams, setNavigationParams] = useState(null);

  const navigate = useNavigate();

  // Enhanced setActiveSection function
  const handleSetActiveSection = useCallback((section, params = null) => {
    setActiveSection(section);
    if (params) {
      setNavigationParams(params);
    } else {
      setNavigationParams(null);
    }
  }, []);

  // Toggle sidebar function
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  // Close sidebar function
  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const triggerInboxRefresh = useCallback(() => {
    setInboxRefreshTrigger((prev) => prev + 1);
  }, []);

  const goToProfile = () => {
    handleSetActiveSection("profile");
  };

  const user = useMemo(() => {
    const userData = JSON.parse(localStorage.getItem("user")) || {};
    const fullName = userData.name || "";
    const [firstName = "", lastName = ""] = fullName.split(" ", 2);
    return {
      id: userData.id || "N/A",
      firstName,
      lastName,
      name: userData.name || "",
      status: userData.status || "N/A",
      role: userData.role || "N/A",
      email: userData.email || "N/A",
      mobile: userData.mobile || "N/A",
    };
  }, []);

  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // When leaving Add Student section, clear registration-related localStorage
    if (activeSection !== "addStudent") {
      localStorage.removeItem("register_step1");
      localStorage.removeItem("register_step2");
      localStorage.removeItem("register_step3");
      localStorage.removeItem("register_step4");
    }
  }, [activeSection]);

  // Close sidebar when clicking outside

  // Handle escape key to close sidebar
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeSidebar();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeSidebar]);

  // Main content renderer
  const renderContent = useCallback(() => {
    if (activeSection === "analytics") {
      return <AgentAnalyticsDashboard />;
    }
    if (activeSection === "addStudent") {
      const steps = [Step1, Step2, Step3, Step4, Step5];
      const CurrentStep = steps[regStep - 1];
      return (
        <div className="space-y-4">
          <CurrentStep
            onNext={() => setRegStep((s) => Math.min(s + 1, steps.length))}
            onBack={() => setRegStep((s) => Math.max(s - 1, 1))}
          />
        </div>
      );
    }

    if (activeSection === "messages-inbox") {
      return (
        <div className="space-y-4">
          <MessagesInbox
            setActiveSection={handleSetActiveSection}
            refreshTrigger={inboxRefreshTrigger}
          />
        </div>
      );
    }

    if (activeSection === "new-message") {
      return (
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center justify-between"
          >
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <MailPlus size={20} className="text-blue-600" />
              New Message
            </h2>
          </motion.div>
          <NewMessage setActiveSection={handleSetActiveSection} />
        </div>
      );
    }

    if (activeSection === "message-thread") {
      if (!navigationParams) {
        return (
          <div className="space-y-4">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              Error: Missing thread information. Please go back to inbox and try
              again.
            </div>
            <button
              onClick={() => handleSetActiveSection("messages-inbox")}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Inbox
            </button>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center justify-between flex-wrap gap-2"
          >
            <button
              onClick={() => handleSetActiveSection("messages-inbox")}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors text-sm"
            >
              <ArrowLeft size={16} /> Back to Inbox
            </button>
            <h2 className="text-lg font-bold text-gray-800">
              Conversation with {navigationParams.recipientName || "User"}
            </h2>
            <div></div>
          </motion.div>
          <MessageThread
            threadId={navigationParams.threadId}
            recipientId={navigationParams.recipientId}
            currentUserId={navigationParams.currentUserId}
            recipientName={navigationParams.recipientName}
            setActiveSection={handleSetActiveSection}
            triggerInboxRefresh={triggerInboxRefresh}
          />
        </div>
      );
    }

    if (activeSection.startsWith("message-thread/")) {
      const threadId = activeSection.split("/")[1];
      return (
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center justify-between flex-wrap gap-2"
          >
            <button
              onClick={() => handleSetActiveSection("messages-inbox")}
              className="flex items-center gap-2 text-blue-600 text-sm"
            >
              <ArrowLeft size={16} /> Back to Inbox
            </button>
            <h2 className="text-lg font-bold text-gray-800">Conversation</h2>
            <div></div>
          </motion.div>
          <MessageThread
            threadId={threadId}
            setActiveSection={handleSetActiveSection}
            triggerInboxRefresh={triggerInboxRefresh}
          />
        </div>
      );
    }

    if (activeSection === "students") {
      return <AllStudentsForm theme={THEME} />;
    }

    if (activeSection === "profile") {
      return (
        <div className="bg-white shadow-sm rounded-lg p-4 max-w-4xl mx-auto">
          <h2 className="text-lg font-bold mb-4 text-gray-800">
            Agent Profile
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">
                  Full Name
                </label>
                <div className="text-sm text-gray-800 border border-gray-200 rounded px-3 py-2 bg-gray-50">
                  {user.name}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">
                  Email
                </label>
                <div className="text-sm text-gray-800 border border-gray-200 rounded px-3 py-2 bg-gray-50">
                  {user.email}
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">
                  Role
                </label>
                <div className="text-sm text-gray-800 border border-gray-200 rounded px-3 py-2 bg-gray-50">
                  {user.role}
                </div>
              </div>
            </div>
          </div>
          <hr className="my-6" />
          <section className="bg-gray-50 rounded-lg p-4 shadow-inner">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Change Password
            </h2>
            <ChangeP email={user?.email} />
          </section>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        <p>Select an option from the sidebar to begin</p>
      </div>
    );
  }, [
    activeSection,
    regStep,
    user,
    navigationParams,
    inboxRefreshTrigger,
    handleSetActiveSection,
    triggerInboxRefresh,
  ]);

  // Hamburger Menu Button Component
  const HamburgerMenu = useMemo(
    () => (
      <motion.button
        ref={hamburgerRef}
        onClick={toggleSidebar}
        className="
  p-2 rounded-xl bg-blue-600/20 
  text-blue-300 
  backdrop-blur-md 
  border border-blue-500/20
  hover:bg-blue-600/30 
  hover:text-white
  transition-all duration-300
  focus:outline-none 
  focus:ring-2 focus:ring-blue-400/50
  active:scale-95
"
        aria-label={sidebarOpen ? "Close menu" : "Open menu"}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          {sidebarOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X size={18} />
            </motion.div>
          ) : (
            <motion.div
              key="menu"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Menu size={18} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    ),
    [sidebarOpen, toggleSidebar]
  );

  // Top bar for mobile
  const TopBar = useMemo(
    () => (
      <div className="fixed top-0 left-0 right-0 z-40 p-3 flex items-center justify-between md:hidden bg-white shadow-sm border-b border-gray-200">
        {HamburgerMenu}
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500"></span>
          <span className="text-sm font-medium text-gray-700">
            Hello, {user.firstName}
          </span>
        </div>
      </div>
    ),
    [user.firstName, HamburgerMenu]
  );

  // Sidebar profile snippet
  const UserProfile = useMemo(
    () => (
      <motion.div
        className="relative flex flex-col items-center bg-gradient-to-br from-gray-800 via-gray-900 to-black p-3 rounded-lg border border-gray-700 min-h-[140px] w-full"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        {/* Profile Image */}
        <div className="relative">
          <Profile userId={user.id} />
          {/* Static Status Dot */}
          <span className="absolute bottom-1 right-1 w-2 h-2 bg-green-500 border border-gray-900 rounded-full"></span>
        </div>

        {/* User Name */}
        <h2 className="mt-2 text-white text-sm font-semibold text-center px-2 line-clamp-2">
          {user.name}
        </h2>

        {/* Role */}
        <p className="mt-1 text-xs text-blue-300 bg-blue-900/30 px-2 py-1 rounded-full">
          {user.role}
        </p>
      </motion.div>
    ),
    [user]
  );

  // Sidebar Link Component
  const SidebarLink = useCallback(
    ({ icon: Icon, label, sectionKey }) => (
      <motion.button
        onClick={() => {
          handleSetActiveSection(sectionKey);
          if (!isDesktop) {
            closeSidebar();
          }
        }}
        className={`w-full text-left flex items-center gap-2 text-sm py-2 px-3 transition-colors duration-300 rounded-lg ${
          activeSection === sectionKey
            ? "bg-blue-600 text-white"
            : "hover:bg-gray-700 text-gray-100"
        }`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={spring}
        aria-current={activeSection === sectionKey ? "page" : undefined}
      >
        <Icon size={14} aria-hidden="true" />
        <span className="truncate">{label}</span>
      </motion.button>
    ),
    [activeSection, handleSetActiveSection, isDesktop, closeSidebar]
  );

  // Sidebar navigation
  const SidebarContent = useMemo(
    () => (
      <div className="overflow-y-auto flex flex-col h-full">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between p-3 border-b border-gray-700">
          <span className="text-white font-semibold text-sm">Menu</span>
          {HamburgerMenu}
        </div>

        {/* User Profile Section */}
        <div className="px-2 py-3">{UserProfile}</div>

        {/* Navigation Menu */}
        <nav className="px-1 py-1 flex-grow space-y-1">
          <SidebarLink icon={User} label="Dashboard" sectionKey="analytics" />
          <SidebarLink icon={User} label="Profile" sectionKey="profile" />
          <SidebarLink icon={Users} label="Students" sectionKey="students" />
          <SidebarLink
            icon={User}
            label="Add Student"
            sectionKey="addStudent"
          />

          <CollapsibleMenu
            title="Messages"
            icon={MessageSquare}
            keyName="messages"
            items={[
              { icon: Mail, label: "Inbox", key: "messages-inbox" },
              { icon: MailPlus, label: "New Message", key: "new-message" },
            ]}
            activeSection={activeSection}
            setActiveSection={handleSetActiveSection}
            setSidebarOpen={setSidebarOpen}
          />
        </nav>

        {/* Logout Section */}
        <div className="mt-auto p-2">
          <LogoutSection navigate={navigate} />
        </div>
      </div>
    ),
    [
      UserProfile,
      activeSection,
      navigate,
      HamburgerMenu,
      SidebarLink,
      handleSetActiveSection,
    ]
  );

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
      {TopBar}

      <AnimatePresence>
        {sidebarOpen && !isDesktop && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 z-40 bg-gray-900 shadow-xl flex flex-col
              w-64 md:relative md:flex-shrink-0`}
        initial={false}
        animate={{
          x: sidebarOpen ? 0 : -260, // FULL animation control
          width: isDesktop
            ? sidebarOpen
              ? 256 // 64 * 4 (Tailwind 'w-64')
              : 0
            : 256,
        }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 30,
        }}
      >
        {SidebarContent}
      </motion.aside>

      {/* Main Content */}
      <div
        className={`flex flex-col flex-1 overflow-hidden transition-all duration-300 pt-12 md:pt-0 min-h-screen`}
      >
        {/* Desktop Header with Hamburger */}
        <div className="hidden md:flex relative items-center bg-gray-900 shadow-lg border-b border-gray-800/40 py-3 px-6">
          <div className="flex items-center gap-4">
            {HamburgerMenu}

            <div className="flex items-center gap-3">
              {/* Logo */}
              <img
                src={`${logo}`}
                alt="GCRD Logo"
                className="h-7 w-auto transition-all duration-300 hover:scale-110 drop-shadow-lg filter"
              />
            </div>
          </div>

          {/* This pushes user info to far right */}
          <div className="flex-1"></div>

          {/* User Info */}
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
            </span>
            <span className="text-sm font-medium text-gray-200">
              Hello, {user.firstName}
            </span>
          </div>
        </div>

        <motion.header
          className="p-3 md:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.6 } }}
        >
          <h1 className="text-base md:text-lg font-bold text-gray-800 flex flex-wrap items-center gap-1">
            <span>Agent Dashboard</span>
            <span className="text-gray-600 font-normal text-sm">
              -{" "}
              {activeSection === "addStudent"
                ? "Add Student"
                : activeSection === "analytics"
                ? "Analytics"
                : activeSection === "profile"
                ? "Profile"
                : activeSection.startsWith("messages")
                ? "Messages"
                : "Overview"}
            </span>
          </h1>
        </motion.header>

        <motion.main
          className="flex-1 p-3 md:p-4 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 min-h-[calc(100vh-8rem)]">
            {renderContent()}
          </div>
        </motion.main>

        <Footer />
      </div>
    </div>
  );
}
